import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useQuery } from '@apollo/client'
import { Logo, Icon } from '@scentregroup/ui'

import { Link as LinkType } from '../../../types/link'

import analyticsUtils from '../../helpers/analytics-utils'
import trackEvent from '../../helpers/analytics-utils/track-event'

import Search, { AlgoliaApiKeys } from '../search'
import SearchButton from '../search-button'

import { Centre } from '../../../types/centre'
import { User } from '../../../types/user'
import { useRecentCentres } from '../../hooks/use-recent-centres'
// import index file directly until legacy website is retired and the
// next.config.js webpack hijacking is removed
import * as withCentreNav from '../../containers/with-centre-nav/index'
import style from './index.module.scss'
import Nav from './nav/main'
import Account from './nav/account'
import SearchBar from './search-bar'
import W from './w.inline.svg'
import { WithNavigationMenu, LinkItem } from './types'
import { LinkContext } from './context'
import { universalHeaderId } from './universal-header-id'

const MAX_PINNED_LINKS = 5
const HOME_LINK_LABEL = 'Go to homepage'

type UniversalHeaderLogo = 'direct-digital'

type NavState = boolean | string[]

export const UniversalHeader: React.FC<{
  Link: LinkType
  logo?: UniversalHeaderLogo
  country: string
  // Used within the Find A Centre selector
  centres?: Centre[]
  // When specified, will show the centre's `navigationMenu` items in the Nav
  centre?: Centre & WithNavigationMenu
  giftCards?: WithNavigationMenu
  title?: LinkItem
  cart?: {
    url: string
    count?: number
  }
  // A set of links to show immediately below the header
  pinnedLinks?: {
    items: LinkItem[]
    hideMore?: boolean
    maxPinnedLinks?: number
  }
  account?: {
    from?: string
    redirect?: string
  }
  // The currently logged in user
  user?: User
  // Navigation structure to show in the "Shop" section
  nationalNav?: WithNavigationMenu
  algoliaApiKeys?: AlgoliaApiKeys
  navPath?: string[]
}> = ({
  Link,
  logo,
  country,
  centres,
  centre: inputCentre,
  giftCards,
  title,
  cart,
  pinnedLinks,
  account,
  user,
  nationalNav,
  algoliaApiKeys,
  navPath,
}) => {
  const universalHeaderRef = useRef<HTMLDivElement>(null)

  const [isNav, setIsNav] = useState<NavState>(navPath || false)

  const toggleScroll = (enableScroll: NavState): void => {
    if (enableScroll) {
      document.body.classList.add(style.noScroll)
    } else {
      document.body.classList.remove(style.noScroll)
    }
  }

  const setNav = useCallback((navPath: NavState): void => {
    toggleScroll(navPath)
    setIsNav(navPath)
  }, [])

  useEffect(() => navPath && setNav(navPath), [navPath, setNav])

  const recentCentres = useRecentCentres()

  // When there's no centre, we attempt to fetch data for the most recently
  // visited centre. Otherwise, fall back to showing national navigation data.
  const centreNavQuery = withCentreNav.mapDataToProps(
    useQuery(withCentreNav.query, {
      ...(withCentreNav.mapPropsToOptions({
        slug: recentCentres[0],
        country,
      }) as any),
      skip: Boolean(inputCentre) || !recentCentres.length,
      context: { version: 2 },
    })
  )

  const centre = centreNavQuery?.centre ?? inputCentre

  const openNav = useCallback(() => {
    setNav(true)
    trackEvent(
      analyticsUtils.categories.UNIVERSAL_HEADER,
      analyticsUtils.actions.UNIVERSAL_HEADER_NAV_OPEN,
      'L0'
    )
  }, [setNav])

  const closeNav = useCallback(() => {
    setNav(false)
    trackEvent(
      analyticsUtils.categories.UNIVERSAL_HEADER,
      analyticsUtils.actions.UNIVERSAL_HEADER_NAV_CLOSE,
      'L0'
    )
  }, [setNav])

  const [isAccount, setIsAccount] = useState(false)

  const setAccountNav = useCallback((displayNav: boolean) => {
    toggleScroll(displayNav)
    setIsAccount(displayNav)
  }, [])

  useEffect(() => {
    const el = universalHeaderRef.current || document.createElement('div')

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(
      ([e]) => el.classList.toggle(style.stuck, e.intersectionRatio < 1),
      { threshold: [1] }
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <div
        id={universalHeaderId}
        ref={universalHeaderRef}
        className={classNames(style['universal-header'])}
      >
        <div className={classNames(style['header-container'])}>
          <div className={classNames(style['header-contents'])}>
            <button
              className={classNames(style.link, style.desktop)}
              data-test-id="univ-hamburger-button"
              aria-label="Side Navigation"
              onClick={openNav}
            >
              <Icon
                size="medium"
                type="hamburger"
                className={classNames(style.icon)}
              />
            </button>
            {title ? (
              <>
                <Link
                  to="/"
                  className={classNames(style.link, style.desktop)}
                  aria-label={HOME_LINK_LABEL}
                >
                  {logo ? <Logo type={logo} className={style.logo} /> : <W />}
                </Link>
                <Link
                  to="/"
                  className={classNames(
                    style.link,
                    style.mobile,
                    style.unstuck
                  )}
                  aria-label={HOME_LINK_LABEL}
                >
                  {logo ? <Logo type={logo} className={style.logo} /> : <W />}
                </Link>
                <Link
                  to={title.url}
                  className={classNames(style.title, style.link)}
                >
                  {title.label}
                </Link>
              </>
            ) : (
              <Link
                to="/"
                className={classNames(style.link)}
                aria-label={HOME_LINK_LABEL}
              >
                <Logo
                  type={logo}
                  className={classNames(style.logo, style.desktop)}
                />
                <Logo
                  type={logo}
                  className={classNames(
                    style.logo,
                    style.mobile,
                    style.unstuck
                  )}
                />
                {logo ? (
                  <Logo
                    type={logo}
                    className={classNames(
                      style.logo,
                      style.mobile,
                      style.stuck
                    )}
                  />
                ) : (
                  <W
                    className={classNames(
                      style.logo,
                      style.mobile,
                      style.stuck
                    )}
                  />
                )}
              </Link>
            )}
            <div className={classNames(style.icons)}>
              {algoliaApiKeys && (
                <>
                  <div className={classNames(style.desktop)}>
                    <Search
                      algoliaApiKeys={algoliaApiKeys}
                      centre={inputCentre}
                      country={country}
                    >
                      {showSearch => (
                        <SearchBar
                          showSearch={showSearch}
                          className={classNames(style.search)}
                        />
                      )}
                    </Search>
                  </div>
                  <Search
                    algoliaApiKeys={algoliaApiKeys}
                    centre={inputCentre}
                    country={country}
                  >
                    {showSearch => (
                      <SearchButton
                        showSearch={showSearch}
                        className={classNames(
                          style.icon,
                          style.mobile,
                          style.stuck
                        )}
                      >
                        <Icon size="medium" type="search" />
                      </SearchButton>
                    )}
                  </Search>
                </>
              )}
              <button
                className={classNames(
                  user?.firstName && style.avatar,
                  style.link
                )}
                data-test-id="account-icon"
                aria-label="Account Navigation"
                onClick={() => setAccountNav(true)}
              >
                {user?.firstName ? (
                  user.firstName.charAt(0).toUpperCase()
                ) : (
                  <Icon
                    size="medium"
                    type="user"
                    className={classNames(style.icon)}
                  />
                )}
              </button>
              {cart && (
                <Link
                  title="Cart"
                  to={cart.url}
                  className={classNames(style.link, style.icon, {
                    [style['cart-active']]: Boolean(cart.count),
                  })}
                >
                  <Icon
                    size="medium"
                    type="shopping-cart"
                    className={classNames(style.icon)}
                  />
                </Link>
              )}
              <button
                className={classNames(style.link, style.mobile)}
                aria-label="Side Navigation"
                onClick={openNav}
              >
                <Icon
                  size="medium"
                  type="hamburger"
                  className={classNames(style.icon)}
                />
              </button>
            </div>
          </div>
        </div>
        {pinnedLinks && (
          <div className={classNames(style['links-container'], style.desktop)}>
            <div className={classNames(style['links-contents'])}>
              {pinnedLinks.items
                .slice(
                  0,
                  pinnedLinks.maxPinnedLinks
                    ? pinnedLinks.maxPinnedLinks
                    : MAX_PINNED_LINKS
                )
                .map((item, i) => (
                  <Link
                    key={i}
                    to={item.url}
                    className={classNames(style.link)}
                    data-test-id="sub-nav-bar-item"
                  >
                    {item.label}
                  </Link>
                ))}
              {!pinnedLinks.hideMore && (
                <button
                  className={classNames(style.link)}
                  aria-label="Side Navigation"
                  onClick={openNav}
                >
                  More...
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={classNames(style.search, style.mobile)}>
        {algoliaApiKeys && (
          <Search
            algoliaApiKeys={algoliaApiKeys}
            centre={inputCentre}
            country={country}
          >
            {showSearch => <SearchBar showSearch={showSearch} />}
          </Search>
        )}
      </div>
      {isNav && (
        <LinkContext.Provider
          value={({ onClick, ...props }) => (
            <Link
              onClick={e => {
                onClick?.(e)
                setNav(false)
              }}
              {...props}
            />
          )}
        >
          <Nav
            country={country}
            centres={centres}
            centre={centre}
            shop={nationalNav}
            giftCards={giftCards}
            initialPath={Array.isArray(isNav) ? isNav : undefined}
            onClose={closeNav}
          />
        </LinkContext.Provider>
      )}
      {isAccount && (
        <LinkContext.Provider
          value={({ onClick, ...props }) => (
            <Link
              onClick={e => {
                onClick?.(e)
                setAccountNav(false)
              }}
              {...props}
            />
          )}
        >
          <Account
            {...account}
            user={user}
            onClose={() => setAccountNav(false)}
          />
        </LinkContext.Provider>
      )}
      <div id="main-content" />
    </>
  )
}
